import { useEffect, useMemo, useRef } from "react";
import { ArrowLeft, ArrowRight, ImagePlus, Star, X } from "lucide-react";

import { cn } from "@/utils/cn";

interface ImagePreviewGridProps {
  images: File[];
  onChange: (images: File[]) => void;
  maxImages?: number;
}

/**
 * docs/ui/component-inventory.md's `ImagePreviewGrid`/`MediaPreviewThumb`:
 * the first image is always the listing's cover/"MAIN" photo — there's no
 * separate `coverIndex` to track, moving a thumb to index 0 *is* setting it
 * as cover. Reordering is left/right arrow buttons rather than full
 * drag-and-drop: same end result (a seller can put any photo first), a
 * fraction of the implementation, and works identically on touch and mouse
 * without a separate mobile "tap-to-reorder" mode to build.
 */
export function ImagePreviewGrid({ images, onChange, maxImages = 8 }: ImagePreviewGridProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const objectUrls = useMemo(() => images.map((file) => URL.createObjectURL(file)), [images]);

  useEffect(() => {
    return () => {
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [objectUrls]);

  function handleFilesSelected(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    const incoming = Array.from(fileList);
    const combined = [...images, ...incoming].slice(0, maxImages);
    onChange(combined);
    if (inputRef.current) inputRef.current.value = "";
  }

  function removeAt(index: number) {
    onChange(images.filter((_, i) => i !== index));
  }

  function moveTo(index: number, direction: -1 | 1) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= images.length) return;
    const next = [...images];
    const [moved] = next.splice(index, 1);
    if (moved) next.splice(targetIndex, 0, moved);
    onChange(next);
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {images.map((file, index) => (
          <div
            key={`${file.name}-${file.lastModified}-${index}`}
            className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-muted"
          >
            <img
              src={objectUrls[index]}
              alt={`Item ${index + 1} of ${images.length}`}
              className="h-full w-full object-cover"
            />
            {index === 0 && (
              <span className="absolute left-1.5 top-1.5 flex items-center gap-1 rounded bg-primary px-1.5 py-0.5 text-caption font-medium text-primary-foreground">
                <Star className="h-3 w-3 fill-current" aria-hidden="true" />
                Cover
              </span>
            )}
            <button
              type="button"
              aria-label={`Remove photo ${index + 1}`}
              onClick={() => removeAt(index)}
              className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm hover:bg-background"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            <div className="absolute bottom-1.5 left-1.5 right-1.5 flex justify-between opacity-0 transition-opacity group-hover:opacity-100">
              <button
                type="button"
                aria-label={`Move photo ${index + 1} earlier`}
                disabled={index === 0}
                onClick={() => moveTo(index, -1)}
                className="flex h-6 w-6 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm hover:bg-background disabled:pointer-events-none disabled:opacity-40"
              >
                <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={`Move photo ${index + 1} later`}
                disabled={index === images.length - 1}
                onClick={() => moveTo(index, 1)}
                className="flex h-6 w-6 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm hover:bg-background disabled:pointer-events-none disabled:opacity-40"
              >
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          </div>
        ))}

        {images.length < maxImages && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className={cn(
              "flex aspect-square flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary",
            )}
          >
            <ImagePlus className="h-6 w-6" aria-hidden="true" />
            <span className="text-body-sm">Add photo</span>
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="sr-only"
        onChange={(event) => handleFilesSelected(event.target.files)}
      />

      <p className="text-caption text-muted-foreground">
        {images.length} of {maxImages} photos. The first photo is the cover shown on cards and
        search results.
      </p>
    </div>
  );
}
